import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

/* Solve a triangle from 3 known parts (SSS / SAS / ASA / AAS / SSA) */
function solve({ mode, a, b, c, A, B, C }) {
  let sides = { a, b, c };
  let angles = { A, B, C };
  const acosD = v => Math.acos(Math.max(-1, Math.min(1, v))) * DEG;

  if (mode === 'sss') {
    if (a + b <= c || a + c <= b || b + c <= a) return null;
    angles.A = acosD((b*b + c*c - a*a) / (2*b*c));
    angles.B = acosD((a*a + c*c - b*b) / (2*a*c));
    angles.C = 180 - angles.A - angles.B;
  } else if (mode === 'sas') {
    // Given sides a, b and included angle C
    sides.c = Math.sqrt(a*a + b*b - 2*a*b*Math.cos(C*RAD));
    angles.A = acosD((sides.b*sides.b + sides.c*sides.c - sides.a*sides.a) / (2*sides.b*sides.c));
    angles.B = 180 - angles.A - C;
  } else if (mode === 'asa') {
    // Angles A, B and included side c
    angles.C = 180 - A - B;
    sides.a = c * Math.sin(A*RAD) / Math.sin(angles.C*RAD);
    sides.b = c * Math.sin(B*RAD) / Math.sin(angles.C*RAD);
  } else if (mode === 'aas') {
    // Angles A, B and non-included side a
    angles.C = 180 - A - B;
    sides.b = a * Math.sin(B*RAD) / Math.sin(A*RAD);
    sides.c = a * Math.sin(angles.C*RAD) / Math.sin(A*RAD);
  } else if (mode === 'ssa') {
    // Two sides a, b and non-included angle A opposite side a
    const sinB = b * Math.sin(A*RAD) / a;
    if (Math.abs(sinB) > 1) return null;
    angles.B = Math.asin(sinB) * DEG;
    angles.C = 180 - A - angles.B;
    sides.c = a * Math.sin(angles.C*RAD) / Math.sin(A*RAD);
  }
  const s = (sides.a + sides.b + sides.c) / 2;
  const area = Math.sqrt(Math.max(0, s * (s-sides.a) * (s-sides.b) * (s-sides.c)));
  const perim = sides.a + sides.b + sides.c;
  return { ...sides, ...angles, s, area, perim };
}

/* Build triangle SVG coordinates given the 3 sides */
function coords(a, b, c) {
  if (!a || !b || !c) return null;
  const cx = (b*b - a*a + c*c) / (2*c);
  const cy = Math.sqrt(Math.max(0, b*b - cx*cx));
  const pad = 50, w = 360, h = 240;
  const xs = [0, c, cx], ys = [0, 0, cy];
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const scale = Math.min((w - 2*pad)/(maxX - minX || 1), (h - 2*pad)/(maxY - minY || 1));
  const tx = v => pad + (v - minX)*scale;
  const ty = v => h - pad - (v - minY)*scale;
  return { A: [tx(0), ty(0)], B: [tx(c), ty(0)], C: [tx(cx), ty(cy)] };
}

export default function TriangleCalc() {
  const [mode, setMode] = useState('sss');
  const [a, setA] = useState(6);
  const [b, setB] = useState(8);
  const [c, setC] = useState(10);
  const [A, setAngA] = useState(60);
  const [B, setAngB] = useState(80);
  const [C, setAngC] = useState(40);

  const r = useMemo(() => solve({ mode, a, b, c, A, B, C }), [mode, a, b, c, A, B, C]);
  const co = r ? coords(r.a, r.b, r.c) : null;

  const modes = [
    { key: 'sss', label: 'SSS — three sides' },
    { key: 'sas', label: 'SAS — two sides + included angle' },
    { key: 'asa', label: 'ASA — two angles + included side' },
    { key: 'aas', label: 'AAS — two angles + non-included side' },
    { key: 'ssa', label: 'SSA — two sides + non-included angle' },
  ];

  return (
    <CalcShell look="cnet" title="Triangle Calculator" subtitle="Solve any triangle from 3 known parts — SSS, SAS, ASA, AAS, or SSA — with step-by-step working.">
      <div className="field"><label>Known parts</label>
        <select value={mode} onChange={e => setMode(e.target.value)} data-testid="tri-mode">
          {modes.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
        <div>
          {mode === 'sss' && <>
            <F label="Side a" v={a} on={setA} tid="tri-a"/><F label="Side b" v={b} on={setB} tid="tri-b"/><F label="Side c" v={c} on={setC} tid="tri-c"/>
          </>}
          {mode === 'sas' && <>
            <F label="Side a" v={a} on={setA} tid="tri-a"/><F label="Side b" v={b} on={setB} tid="tri-b"/><F label="Angle C (deg)" v={C} on={setAngC} tid="tri-C"/>
          </>}
          {mode === 'asa' && <>
            <F label="Angle A (deg)" v={A} on={setAngA} tid="tri-A"/><F label="Angle B (deg)" v={B} on={setAngB} tid="tri-B"/><F label="Side c" v={c} on={setC} tid="tri-c"/>
          </>}
          {mode === 'aas' && <>
            <F label="Angle A (deg)" v={A} on={setAngA} tid="tri-A"/><F label="Angle B (deg)" v={B} on={setAngB} tid="tri-B"/><F label="Side a" v={a} on={setA} tid="tri-a"/>
          </>}
          {mode === 'ssa' && <>
            <F label="Side a" v={a} on={setA} tid="tri-a"/><F label="Side b" v={b} on={setB} tid="tri-b"/><F label="Angle A (deg)" v={A} on={setAngA} tid="tri-A"/>
          </>}

          {!r ? <div className="text-red-600 text-sm mt-2">Invalid — the given parts don't form a valid triangle.</div> :
            <>
              {(() => {
                const angles = [r.A, r.B, r.C].sort((a,b) => a - b);
                const sides = [r.a, r.b, r.c].sort((a,b) => a - b);
                const [a1, a2, a3] = angles;
                const [s1, s2, s3] = sides;
                const isRight = Math.abs(a3 - 90) < 0.05;
                const isObtuse = a3 > 90.05;
                const isAcute = a3 < 89.95;
                const equilateral = Math.abs(s1 - s3) < 1e-4;
                const isosceles = !equilateral && (Math.abs(s1 - s2) < 1e-4 || Math.abs(s2 - s3) < 1e-4);
                const bySides = equilateral ? 'Equilateral' : isosceles ? 'Isosceles' : 'Scalene';
                const byAngle = isRight ? 'Right' : isObtuse ? 'Obtuse' : isAcute ? 'Acute' : '';
                return (
                  <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm" data-testid="tri-classification">
                    This is a <b className="text-blue-800">{byAngle} {bySides} triangle</b>
                    <span className="text-xs text-slate-600"> · classified by sides ({bySides.toLowerCase()}) and by angles ({byAngle.toLowerCase()})</span>
                  </div>
                );
              })()}
              <table className="data-table mt-3" data-testid="tri-result-table"><tbody>
              <tr><td>Angle A (opposite side a)</td><td className="text-right font-semibold">{r.A.toFixed(3)}°</td></tr>
              <tr><td>Angle B (opposite side b)</td><td className="text-right font-semibold">{r.B.toFixed(3)}°</td></tr>
              <tr><td>Angle C (opposite side c)</td><td className="text-right font-semibold">{r.C.toFixed(3)}°</td></tr>
              <tr><td>Side a</td><td className="text-right">{r.a.toFixed(4)}</td></tr>
              <tr><td>Side b</td><td className="text-right">{r.b.toFixed(4)}</td></tr>
              <tr><td>Side c</td><td className="text-right">{r.c.toFixed(4)}</td></tr>
              <tr><td>Semi-perimeter (s)</td><td className="text-right">{r.s.toFixed(4)}</td></tr>
              <tr><td>Perimeter</td><td className="text-right font-semibold">{r.perim.toFixed(4)}</td></tr>
              <tr><td>Area (Heron)</td><td className="text-right font-bold text-blue-700">{r.area.toFixed(4)}</td></tr>
            </tbody></table></>}
        </div>
        <div className="bg-slate-50 rounded-2xl p-4">
          {co && (() => {
            const cx3 = (co.A[0] + co.B[0] + co.C[0]) / 3;
            const cy3 = (co.A[1] + co.B[1] + co.C[1]) / 3;
            const angleArc = (P, Q, R, ang, colour) => {
              // Small arc at vertex P between rays PQ and PR
              const rad = 22;
              const v1 = [Q[0] - P[0], Q[1] - P[1]];
              const v2 = [R[0] - P[0], R[1] - P[1]];
              const n1 = Math.hypot(...v1), n2 = Math.hypot(...v2);
              const p1 = [P[0] + rad * v1[0] / n1, P[1] + rad * v1[1] / n1];
              const p2 = [P[0] + rad * v2[0] / n2, P[1] + rad * v2[1] / n2];
              const large = ang > 180 ? 1 : 0;
              return <path d={`M${p1[0]},${p1[1]} A${rad},${rad} 0 ${large} 0 ${p2[0]},${p2[1]}`} fill="none" stroke={colour} strokeWidth="1.5" />;
            };
            return (
              <svg viewBox="0 0 360 240" className="w-full">
                <polygon
                  points={`${co.A.join(',')} ${co.B.join(',')} ${co.C.join(',')}`}
                  fill="#dbeafe"
                  stroke="#2563eb"
                  strokeWidth="2.5"
                  style={{ transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
                {/* Green dashed altitude from C perpendicular to AB */}
                <line x1={co.C[0]} y1={co.C[1]} x2={co.C[0]} y2={co.A[1]} stroke="#4a7c1f" strokeWidth="1.2" strokeDasharray="4 3" />
                <text x={co.C[0] + 4} y={(co.C[1] + co.A[1]) / 2} fontSize="11" fill="#4a7c1f" fontStyle="italic">altitude</text>

                {/* Angle arcs */}
                {angleArc(co.A, co.B, co.C, r.A, '#dc2626')}
                {angleArc(co.B, co.C, co.A, r.B, '#059669')}
                {angleArc(co.C, co.A, co.B, r.C, '#7c3aed')}
                <text x={co.A[0] + 18} y={co.A[1] + 4} fontSize="10" fill="#dc2626">{r.A.toFixed(1)}°</text>
                <text x={co.B[0] - 40} y={co.B[1] + 4} fontSize="10" fill="#059669">{r.B.toFixed(1)}°</text>
                <text x={co.C[0] - 12} y={co.C[1] + 22} fontSize="10" fill="#7c3aed">{r.C.toFixed(1)}°</text>

                {/* Vertices */}
                {[['A', co.A, '#1e40af'], ['B', co.B, '#1e40af'], ['C', co.C, '#1e40af']].map(([n, p, col]) => (
                  <g key={n}>
                    <circle cx={p[0]} cy={p[1]} r="5" fill={col} style={{ transition: 'cx 400ms cubic-bezier(0.4, 0, 0.2, 1), cy 400ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
                    <text x={p[0] + (n === 'B' ? 8 : -14)} y={p[1] + (n === 'C' ? -8 : 16)} fontSize="14" fontWeight="bold" fill={col}>{n}</text>
                  </g>
                ))}
                <text x={(co.A[0] + co.B[0]) / 2} y={co.A[1] + 22} fontSize="12" fill="#4a7c1f" fontStyle="italic">c = {r.c.toFixed(2)}</text>
                <text x={(co.A[0] + co.C[0]) / 2 - 44} y={(co.A[1] + co.C[1]) / 2} fontSize="12" fill="#4a7c1f" fontStyle="italic">b = {r.b.toFixed(2)}</text>
                <text x={(co.B[0] + co.C[0]) / 2 + 8} y={(co.B[1] + co.C[1]) / 2} fontSize="12" fill="#4a7c1f" fontStyle="italic">a = {r.a.toFixed(2)}</text>
              </svg>
            );
          })()}
        </div>
      </div>

      {r && <StepsPanel steps={
        mode === 'sss' ? [
          `Given sides a=${a}, b=${b}, c=${c}. Verify triangle inequality (all satisfied).`,
          `Angle A = cos⁻¹((b² + c² − a²) / (2·b·c)) = cos⁻¹(${((b*b+c*c-a*a)/(2*b*c)).toFixed(4)}) = <b>${r.A.toFixed(3)}°</b>`,
          `Angle B = cos⁻¹((a² + c² − b²) / (2·a·c)) = <b>${r.B.toFixed(3)}°</b>`,
          `Angle C = 180° − A − B = <b>${r.C.toFixed(3)}°</b>`,
          `Semi-perimeter s = (a+b+c)/2 = ${r.s.toFixed(3)}`,
          `Area = √(s(s−a)(s−b)(s−c)) = <b>${r.area.toFixed(4)}</b>`,
        ] : mode === 'sas' ? [
          `Given a=${a}, b=${b}, C=${C}°.`,
          `Law of cosines: c² = a² + b² − 2ab·cos(C) → c = <b>${r.c.toFixed(4)}</b>`,
          `Angle A = cos⁻¹((b²+c²−a²)/(2bc)) = <b>${r.A.toFixed(3)}°</b>; Angle B = 180 − A − C = <b>${r.B.toFixed(3)}°</b>`,
          `Area = ½·a·b·sin(C) = <b>${(0.5*a*b*Math.sin(C*RAD)).toFixed(4)}</b>`,
        ] : mode === 'asa' ? [
          `Given A=${A}°, B=${B}°, c=${c}. Angle C = 180 − A − B = ${r.C.toFixed(3)}°`,
          `Law of sines: a/sin(A) = c/sin(C) → a = ${r.a.toFixed(4)}, b = ${r.b.toFixed(4)}`,
          `Area = ½·a·b·sin(C) = <b>${r.area.toFixed(4)}</b>`,
        ] : mode === 'aas' ? [
          `Given A=${A}°, B=${B}°, a=${a}. Angle C = 180 − A − B = ${r.C.toFixed(3)}°`,
          `Law of sines: b = a·sin(B)/sin(A) = ${r.b.toFixed(4)}; c = a·sin(C)/sin(A) = ${r.c.toFixed(4)}`,
        ] : [
          `Given a=${a}, b=${b}, A=${A}°.`,
          `sin(B) = b·sin(A)/a = ${(b*Math.sin(A*RAD)/a).toFixed(4)} → B = ${r.B.toFixed(3)}°`,
          `Angle C = 180 − A − B = ${r.C.toFixed(3)}°; c = a·sin(C)/sin(A) = ${r.c.toFixed(4)}`,
        ]
      }/>}
    </CalcShell>
  );
}
function F({ label, v, on, tid }) {
  return <div className="field"><label>{label}</label><input type="number" step="0.01" value={v} onChange={e => on(+e.target.value || 0)} data-testid={tid}/></div>;
}
